import SimpleModal from '@broadleaf/admin-components/dist/common/components/SimpleModal';
import React, { useCallback, useState } from 'react';
import cx from 'classnames';
import { utils, unstable } from '@broadleaf/admin-components';
const { RequestUtils } = utils;
const { MetadataUtils } = unstable;
const { getAttribute, getEndpointByType } = MetadataUtils;
const { request } = RequestUtils;

const AuroraPayInvoiceFetch = ({ actionDefinition, contextParams }) => {
  const [disabled, toggleDisabled] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pdfData, setPdfData] = useState(null);
  const [error, setError] = useState(null);

  const submitEndpoint = getEndpointByType(
    actionDefinition,
    'AURORAPAY_INVOICE'
  );

  const fetchInvoice = useCallback(async () => {
    try {
      setError(null);
      toggleDisabled(true);

      const response = await request(
        {
          method: 'get',
          ...submitEndpoint
        },
        contextParams
      );

      try {
        const dataUrl = convertToBlob(response);
        setPdfData(dataUrl);
        setIsModalOpen(true);
      } catch (error) {
        console.error('Error processing PDF data:', error);
        setError('Unable to process the invoice PDF. Please try again later.');
      }
    } catch (error) {
      console.error('Error fetching invoice:', error);

      if (error.response && error.response.status === 400) {
        let errorMessage =
          'Invalid request. Please contact support for assistance.';

        if (error.response.data) {
          try {
            const errorData =
              typeof error.response.data === 'string'
                ? JSON.parse(error.response.data)
                : error.response.data;

            if (errorData.message || errorData.detail) {
              errorMessage = errorData.message || errorData.detail;
            }
          } catch (e) {
            if (typeof error.response.data === 'string') {
              errorMessage = error.response.data;
            }
          }
        }

        setError(errorMessage);
      } else {
        setError('Failed to fetch invoice. Please try again later.');
      }
    } finally {
      toggleDisabled(false);
    }
  }, [contextParams, submitEndpoint]);

  return (
    <>
      <FetchAction
        actionDefinition={actionDefinition}
        fetchInvoice={fetchInvoice}
        disabled={disabled}
      />

      {error && (
        <div className="tw-mt-2 tw-text-sm tw-text-red-600">{error}</div>
      )}

      {isModalOpen && (
        <SimpleModal
          isOpen={isModalOpen}
          onClose={() => {
            // Clean up the object URL to prevent memory leaks
            if (pdfData) {
              URL.revokeObjectURL(pdfData);
            }
            setIsModalOpen(false);
          }}
          title={actionDefinition.label}
          size="xl"
        >
          <div
            id="pdf-display"
            className="tw-h-[80vh] tw-w-full tw-overflow-auto"
          >
            <object
              data={pdfData}
              type="application/pdf"
              width="100%"
              height="100%"
            >
              <p>Unable to display PDF.</p>
            </object>
          </div>
        </SimpleModal>
      )}
    </>
  );
};

const FetchAction = ({ actionDefinition, fetchInvoice, disabled }) => {
  const submitLabel = getAttribute(
    actionDefinition,
    'submitLabel',
    actionDefinition.label
  );

  return (
    <>
      <FetchButton fetchInvoice={fetchInvoice} disabled={disabled}>
        {submitLabel}
      </FetchButton>
    </>
  );
};

const FetchButton = ({ children, disabled = false, fetchInvoice }) => {
  return (
    <button
      className={cx(
        'fetch-invoice-button focus:tw-shadow-outline tw-flex tw-items-center tw-justify-center tw-rounded tw-border tw-border-gray-200 tw-px-4 tw-py-4 tw-text-sm tw-font-semibold tw-text-blue-700 tw-shadow focus:tw-outline-none md:tw-mb-3 md:tw-py-2 lg:tw-w-full',
        {
          'tw-bg-white hover:tw-bg-blue-100 focus:tw-bg-blue-100 active:tw-bg-blue-200':
            !disabled
        },
        {
          'tw-cursor-not-allowed tw-bg-gray-300': disabled
        }
      )}
      disabled={disabled}
      onClick={fetchInvoice}
      type="button"
    >
      {disabled && <Spinner />}
      {children}
    </button>
  );
};

function convertToBlob(response) {
  const binaryData = convertBase64ToBinary(response.data);
  const blob = new Blob([binaryData], { type: 'application/pdf' });
  return URL.createObjectURL(blob);
}

/**
 * Converts base64 encoded bytes to binary data suitable for a Blob
 * @param {string} base64String - The base64 encoded string
 * @returns {Uint8Array} The binary data
 */
const convertBase64ToBinary = base64String => {
  try {
    const byteCharacters = atob(base64String);
    const byteArray = new Uint8Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteArray[i] = byteCharacters.charCodeAt(i);
    }
    return byteArray;
  } catch (error) {
    console.error('Error converting base64 to binary:', error);
    return new Uint8Array();
  }
};

const Spinner = () => (
  <div className="tw-mr-2 tw-inline-block tw-h-4 tw-w-4 tw-animate-spin tw-rounded-full tw-border-2 tw-border-blue-700 tw-border-t-transparent"></div>
);

export default AuroraPayInvoiceFetch;
export { AuroraPayInvoiceFetch };
